import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import type { DashboardFilters } from "@/features/Dashboard";
import { dashboardService } from "@/features/Dashboard";

// Query Keys
export const PROPOSALS_QUERY_KEYS = {
  all: ["proposals"] as const,
  lists: () => [...PROPOSALS_QUERY_KEYS.all, "list"] as const,
  list: (filters?: DashboardFilters) =>
    [...PROPOSALS_QUERY_KEYS.lists(), { filters }] as const,
  relevant: () => [...PROPOSALS_QUERY_KEYS.all, "relevant"] as const,
  details: () => [...PROPOSALS_QUERY_KEYS.all, "detail"] as const,
  detail: (id: string) => [...PROPOSALS_QUERY_KEYS.details(), id] as const,
};

// Hook principal para propostas com filtros
export function useProposalsQuery(filters?: DashboardFilters) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: PROPOSALS_QUERY_KEYS.list(filters),
    queryFn: () => dashboardService.getProposals(filters),
    enabled: !!user,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos (novo nome para cacheTime)
    refetchOnWindowFocus: false,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Subscrição em tempo real usando useEffect
  useEffect(() => {
    if (!user) return;

    const unsubscribe = dashboardService.subscribeToProposals(
      (proposalsList) => {
        // Atualizar o cache do React Query com dados em tempo real
        queryClient.setQueryData(
          PROPOSALS_QUERY_KEYS.list(filters),
          proposalsList
        );
      },
      (errorMessage) => {
        console.error("Erro na subscrição de propostas:", errorMessage);
        // Invalidar query para tentar recarregar
        queryClient.invalidateQueries({
          queryKey: PROPOSALS_QUERY_KEYS.list(filters),
        });
      },
      filters
    );

    return () => {
      unsubscribe();
    };
  }, [user, filters, queryClient]);

  return {
    proposals: query.data || [],
    loading: query.isLoading,
    error: query.error?.message || null,
    isStale: query.isStale,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

// Hook para propostas relevantes (warm, hot, burning)
export function useRelevantProposalsQuery() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["proposals", "relevant"],
    queryFn: () => dashboardService.getRelevantProposals(),
    enabled: !!user,
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      // Se for erro de índice, não tente novamente
      if (error.message?.includes("index")) {
        return false;
      }
      return failureCount < 3;
    },
  });

  return {
    proposals: query.data || [],
    loading: query.isLoading,
    error: query.error?.message || null,
    isStale: query.isStale,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

// Hook para uma proposta específica
export function useProposalQuery(id: string) {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: PROPOSALS_QUERY_KEYS.detail(id),
    queryFn: async () => {
      // Buscar na lista existente primeiro
      const proposals = await dashboardService.getProposals();
      return proposals.find((p) => p.id === id) || null;
    },
    enabled: !!user && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });

  return {
    proposal: query.data,
    loading: query.isLoading,
    error: query.error?.message || null,
    refetch: query.refetch,
  };
}

// Hook customizado para invalidar cache de propostas
export function useInvalidateProposals() {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: PROPOSALS_QUERY_KEYS.all,
    });
  };

  const invalidateList = (filters?: DashboardFilters) => {
    queryClient.invalidateQueries({
      queryKey: PROPOSALS_QUERY_KEYS.list(filters),
    });
  };

  const invalidateRelevant = () => {
    queryClient.invalidateQueries({
      queryKey: PROPOSALS_QUERY_KEYS.relevant(),
    });
  };

  return {
    invalidateAll,
    invalidateList,
    invalidateRelevant,
  };
}
